import { useState } from 'react';
import { Stack, Group, Button, ActionIcon, Modal, Text } from '@mantine/core';
import { IconPlus, IconEdit, IconTrash } from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import { notifications } from '@mantine/notifications';
import { useCreateShop, useUpdateShop, useDeleteShop } from '@/hooks/useShops';
import { ShopForm } from './ShopForm';
import type { Shop } from '@/types/company';

interface ShopListProps {
  companyId: string;
  shops: Shop[];
}

export function ShopList({ companyId, shops }: ShopListProps) {
  const [formOpened, setFormOpened] = useState(false);
  const [editingShop, setEditingShop] = useState<Shop | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Shop | null>(null);
  const createShop = useCreateShop();
  const updateShop = useUpdateShop();
  const deleteShop = useDeleteShop();

  const handleSave = (data: Omit<Shop, 'id' | 'companyId'>) => {
    if (editingShop) {
      updateShop.mutate(
        { companyId, shopId: editingShop.id, data },
        {
          onSuccess: () => {
            notifications.show({ message: 'Shop aktualisiert.', color: 'green' });
            setFormOpened(false);
            setEditingShop(null);
          },
        },
      );
    } else {
      createShop.mutate(
        { companyId, data },
        {
          onSuccess: () => {
            notifications.show({ message: 'Shop hinzugefügt.', color: 'green' });
            setFormOpened(false);
          },
        },
      );
    }
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteShop.mutate(
      { companyId, shopId: deleteTarget.id },
      {
        onSuccess: () => {
          notifications.show({ message: 'Shop gelöscht.', color: 'green' });
          setDeleteTarget(null);
        },
      },
    );
  };

  return (
    <Stack gap="sm">
      <Group justify="flex-end">
        <Button size="compact-sm" leftSection={<IconPlus size={14} />} onClick={() => { setEditingShop(null); setFormOpened(true); }}>
          Shop hinzufügen
        </Button>
      </Group>

      <DataTable
        withTableBorder
        borderRadius="sm"
        records={shops}
        columns={[
          {
            accessor: 'address',
            title: 'Adresse',
            render: (shop) =>
              typeof shop.address === 'string'
                ? shop.address
                : `${shop.address.street} ${shop.address.houseNumber}, ${shop.address.postalCode} ${shop.address.city}`,
          },
          { accessor: 'email', title: 'E-Mail' },
          { accessor: 'phone', title: 'Telefon' },
          {
            accessor: 'actions',
            title: '',
            width: 80,
            render: (shop) => (
              <Group gap="xs" wrap="nowrap">
                <ActionIcon variant="subtle" onClick={() => { setEditingShop(shop); setFormOpened(true); }}>
                  <IconEdit size={16} />
                </ActionIcon>
                <ActionIcon variant="subtle" color="red" onClick={() => setDeleteTarget(shop)}>
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            ),
          },
        ]}
        noRecordsText="Keine Shops vorhanden"
      />

      <Modal
        opened={formOpened}
        onClose={() => { setFormOpened(false); setEditingShop(null); }}
        title={editingShop ? 'Shop bearbeiten' : 'Neuer Shop'}
      >
        <ShopForm
          initial={editingShop}
          onSave={handleSave}
          onCancel={() => { setFormOpened(false); setEditingShop(null); }}
          loading={createShop.isPending || updateShop.isPending}
        />
      </Modal>

      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Shop löschen">
        <Stack>
          <Text>Möchten Sie diesen Shop wirklich löschen?</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDeleteTarget(null)}>Abbrechen</Button>
            <Button color="red" onClick={handleDelete} loading={deleteShop.isPending}>Löschen</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
